/**
 * @file useFetchFromBackend.js
 * @module src/js/hooks/useFetchFromBackend
 * @description
 * **A hook to fetch data from the backend API with optional authentication handling.**
 * - Sends HTTP requests to specified backend endpoints.
 * - Supports various methods (GET, POST, etc.) and request bodies.
 * - Handles file uploads when specified.
 * - Manages authentication by checking for 401 responses and triggering logout if necessary.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Transform from a pure JS function to a React hook to use useHandleLogout. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import useHandleLogout from "./useHandleLogout";

export default function useFetchFromBackend() {
    const handleLogout = useHandleLogout();

    const fetchFromBackend = useCallback(async ({
        request,
        method = "GET",
        body,
        uploadFile = false,
        requireAuth = false,
        from,
        scrollTo,
        showModal = true,
        navigateToLogin = true,
    }) => {
        if (!request) throw new Error("No request provided");

        try {
            const options = {
                method,
                headers: uploadFile ? {} : { "Content-Type": "application/json" },
                credentials: "include"
            };
            if (body) {
                options.body = uploadFile ? body : JSON.stringify(body);
            }


            const response = await fetch(request, options);
            if (requireAuth && response.status === 401) { // Unauthorized
                await handleLogout({ // must await to ensure logout is completed before proceeding
                    sessionExpired: true,
                    from,
                    scrollTo,
                    showModal,
                    navigateToLogin,
                });
                return; // must return to prevent further execution of this function
            }


            const data = await response.json(); // Convert to JSON
            if (!data) throw new Error("No data received from backend");
            
            return data;
            
        } catch (error) {
            console.error("Error fetching data: ", error);
        }
    }, [handleLogout]);

    return fetchFromBackend;
}
